import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Rectangle,
  XAxis,
  YAxis,
  Brush,
} from 'recharts';
import { getColorByIndex } from '../../utils/colors';
import { getReportParams } from '../../redux/Report/Report.slice';
import { useAppSelector } from '../../hooks/useAppSelector.hook';
import { MultipleChartDataItem, TypedChartProps } from './Chart.component';
import ChartLegendContentComponent from './ChartLegendContent.component';

const CHART_PADDING = 32;
const YAXIS_WIDTH = 200;
const MIN_ROW_HEIGHT_WITH_TEXT = 10;

const StyledBarChartContainer = styled.div`
  aspect-ratio: 1;
  max-height: 100%;
  max-width: 100%;
`;

interface ActiveBar {
  index: number | undefined;
  label?: string | undefined;
  highlightAll?: boolean;
}

const LABEL_COLOR = 'black';
const EXPORT_LEGEND_MARGIN = 100;

const Tip = (props: any) => {
  const { x, y, width, height, valueForDisplay, highlightAll, showLabels } =
    props;

  if (
    !highlightAll ||
    showLabels ||
    (highlightAll && height >= MIN_ROW_HEIGHT_WITH_TEXT)
  ) {
    const mx = x + width / 2 - 3;
    const my = y + height / 2 + 5;

    return (
      <text x={mx} y={my} fill={LABEL_COLOR} fontWeight="bold">
        {valueForDisplay}
      </text>
    );
  }

  return <></>;
};

const BarWithTip = (props: any) => {
  const {
    x,
    y,
    width,
    height,
    fill,
    value,
    renderForRow,
    highlightAll,
    showLabels,
    commonCount,
    payload,
  } = props;

  const blocksInRow = payload.groupCount;
  const blockWidth = value[1] - value[0];
  const oneBlockWidth = width / blockWidth;
  const valueForDisplay = value[1] - value[0];
  const percentValue = `${((blocksInRow * 100) / commonCount).toFixed(1)}%`;
  const isShowTip = renderForRow || highlightAll;

  if (!valueForDisplay) return <></>;

  return (
    <g>
      <Rectangle x={x} y={y} width={width} height={height} fill={fill} />

      {isShowTip && (
        <>
          <Tip
            {...props}
            valueForDisplay={valueForDisplay}
            highlightAll={highlightAll}
            showLabels={showLabels}
          />

          <text
            x={oneBlockWidth + 30}
            y={y + height / 2 + 5}
            fill={LABEL_COLOR}
            fontWeight="bold"
          >
            {percentValue}
          </text>
        </>
      )}
    </g>
  );

};

const HorizontalBarChartComponent = ({
  data,
  stackBlockNames,
  onClick,
  onMultipleClick,
  showLabels,
  legendPortalRef,
  isAnimationActive = true,
  commonCount,
  showLegend = true,
}: TypedChartProps) => {
  const [activeBar, setActiveBar] = useState<ActiveBar>({} as ActiveBar);
  const reportParams = useAppSelector(getReportParams);

  const handleClick = (
    dataItems: [MultipleChartDataItem, MultipleChartDataItem],
  ) => {
    if (onMultipleClick) onMultipleClick(dataItems);
  };

  const handleLabelClick = ({ value }: any) => {
    if (onClick) onClick({ label: value }, reportParams.subGroupBy);
  };

  const ChartLegendContent = useCallback(
    (props: any) => (
      <ChartLegendContentComponent {...props} portalRef={legendPortalRef} />
    ),
    [legendPortalRef],
  );

  return (
    <StyledBarChartContainer>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{
            top: CHART_PADDING + 30,
            bottom: CHART_PADDING,
            left: CHART_PADDING,
            right: CHART_PADDING + 30,
          }}
          layout="vertical"
        >
          <XAxis type="number" axisLine={false} />

          <YAxis
            type="category"
            dataKey="label"
            axisLine={false}
            width={YAXIS_WIDTH}
          />

          {stackBlockNames?.map((blockName, index) => (
            <Bar
              dataKey={blockName}
              stackId="a"
              key={index}
              name={blockName}
              fill={getColorByIndex(index)}
              onClick={props => {
                handleClick([
                  {
                    label: props.label,
                    field: reportParams.groupBy,
                  },
                  {
                    label: blockName,
                    field: reportParams.subGroupBy,
                  },
                ]);
              }}
              onMouseEnter={props => {
                setActiveBar({ index, label: `${props.id}${blockName}` });
              }}
              onMouseLeave={() =>
                setActiveBar({ index: undefined, label: undefined })
              }
              shape={(props: any) =>
                <BarWithTip
                  {...props}
                  renderForRow={`${props.id}${blockName}` === activeBar.label || showLabels}
                  highlightAll={activeBar.highlightAll && index === activeBar.index}
                  showLabels={showLabels}
                  commonCount={commonCount}
                  fill={
                    getColorByIndex(
                      index,
                      activeBar.highlightAll
                        ? index === activeBar.index
                        : `${props.id}${blockName}` === activeBar.label
                    )
                  }
                />
              }
              isAnimationActive={isAnimationActive}
            />
          ))}

          <Brush />

          {showLegend && (
            <Legend
              align="right"
              verticalAlign="middle"
              layout="vertical"
              onClick={handleLabelClick}
              content={(props: any) =>
                ChartLegendContent(
                  Object.assign(
                    { ...props },
                    showLabels && { marginLeft: EXPORT_LEGEND_MARGIN },
                  ),
                )
              }
              onMouseEnter={(payload, index) =>
                setActiveBar({ index, highlightAll: true })
              }
              onMouseLeave={() =>
                setActiveBar({ index: undefined, highlightAll: false })
              }
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </StyledBarChartContainer>
  );
};

export default HorizontalBarChartComponent;
